import { useState } from "react";

function newKey(): string {
  return crypto.randomUUID();
}

/**
 * One idempotency key per intended command, for the endpoints that ask for an `Idempotency-Key`
 * (invitations, cohorts, password change). The rule from the API: replay the SAME key only with the
 * SAME payload (so a retry after an uncertain outcome never creates a second thing), and use a NEW
 * key for a new intention.
 *
 * `fingerprint` stands for the payload: the key stays while it stays, and a change of payload
 * (the person edited the e-mail) draws a new key. `rotate()` draws a new one on demand, after a
 * success or a definitive refusal, so the next attempt is a fresh command.
 */
export function useIdempotencyKey(fingerprint: string): {
  key: string;
  rotate: () => void;
} {
  const [state, setState] = useState(() => ({ fingerprint, key: newKey() }));

  // "A prop changed": adjust state while rendering, so the first submit after an edit never uses
  // the previous payload's key.
  if (state.fingerprint !== fingerprint) {
    setState({ fingerprint, key: newKey() });
  }

  return {
    key: state.key,
    rotate: () => {
      setState((current) => ({ ...current, key: newKey() }));
    },
  };
}
